import { Center, Loader, Stack, Text } from '@mantine/core';

/**
 * LoadingScreen - A reusable loading component with customizable messaging
 *
 * A professional loading screen component that displays a spinner with
 * customizable primary and secondary text messages. Designed to provide
 * consistent loading states across the application.
 *
 * @param {Object} props - Component props
 * @param {string} [props.message='Loading data...'] - Primary loading message
 * @param {string} [props.description='Juice is loading your latest data. Please wait...'] - Secondary descriptive text
 * @param {string} [props.loaderColor='cyan'] - Color of the loading spinner
 * @param {string} [props.loaderSize='lg'] - Size of the loading spinner ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {string} [props.height='100vh'] - Height of the loading container
 *
 * @example
 * // Basic usage with default props
 * <LoadingScreen />
 *
 * @example
 * // Custom message only
 * <LoadingScreen message="Saving changes..." />
 *
 * @example
 * // Custom message and description
 * <LoadingScreen
 *   message="Processing invoices..."
 *   description="Please wait while we process your invoices"
 * />
 *
 * @example
 * // Full customization
 * <LoadingScreen
 *   message="Uploading files..."
 *   description="Your files are being uploaded securely"
 *   loaderColor="green"
 *   loaderSize="xl"
 *   height="50vh"
 * />
 *
 * @example
 * // Context-specific usage
 * <LoadingScreen
 *   message="Loading vendors..."
 *   description="Juice is loading your vendor data. Please wait..."
 * />
 */
const LoadingScreen = ({
  message = 'Loading data...',
  description = 'Juice is loading your latest data. Please wait...',
  loaderColor = 'cyan',
  loaderSize = 'lg',
  height = '100vh',
}) => {
  return (
    <Center style={{ height }}>
      <Stack spacing="xs" align="center" style={{ maxWidth: 640 }}>
        <Loader color={loaderColor} size={loaderSize} />
        <Text size="md" color="dimmed" weight={500}>
          {message}
        </Text>
        <Text size="sm" color="gray" align="center">
          {description}
        </Text>
      </Stack>
    </Center>
  );
};

export default LoadingScreen;
